import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { TextField, SelectField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, StatGrid, FeatureGrid, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-hook-generator")!

export const Route = createFileRoute("/tools/instagram-hook-generator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-hook-generator",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-hook-generator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-hook-generator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-hook-generator" },
      ]}),
      createHowToSchema({ name: "How to Generate Instagram Hooks", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: HookGeneratorPage,
})

// --- Content type and hook style options ---

const contentTypeOptions = [
  { value: "reel", label: "Reel / Short Video" },
  { value: "carousel", label: "Carousel Post" },
  { value: "single-image", label: "Single Image Post" },
  { value: "story", label: "Story" },
]

const hookStyleOptions = [
  { value: "shocking", label: "Shocking / Bold" },
  { value: "curiosity", label: "Curiosity-Driven" },
  { value: "relatable", label: "Relatable / Personal" },
  { value: "controversial", label: "Controversial / Hot Take" },
  { value: "educational", label: "Educational / Value" },
  { value: "storytelling", label: "Storytelling / Narrative" },
]

// --- Hook patterns per style ---

const hookPatterns: Record<string, string[]> = {
  shocking: [
    "Stop doing {topic} if you want to succeed",
    "The {topic} mistake that's costing you everything",
    "I was WRONG about {topic}. Here's the truth.",
    "Delete your {topic} strategy. Here's what works instead.",
    "Nobody is talking about this {topic} problem",
    "{topic} is dead. Here's what replaced it.",
    "I lost everything because of {topic}. Then I discovered this.",
    "The {topic} lie everyone believes",
    "Why 99% of people fail at {topic}",
    "WARNING: {topic} will never be the same",
    "The ugly truth about {topic} no guru will tell you",
    "I quit {topic} for 30 days. What happened next shocked me.",
  ],
  curiosity: [
    "The truth about {topic} that nobody talks about",
    "What would happen if you changed your {topic} approach?",
    "This {topic} trick changed everything for me",
    "Why your {topic} isn't working (and the simple fix)",
    "The secret to {topic} that top creators use",
    "I tested every {topic} strategy. Only this one worked.",
    "What I learned after 30 days of {topic}",
    "The one {topic} hack you've never tried",
    "Why most {topic} advice is completely wrong",
    "Here's what no one tells you about {topic}",
    "What happens when you combine {topic} with consistency",
    "The {topic} shortcut that actually works",
  ],
  relatable: [
    "POV: You finally figured out {topic}",
    "Me trying to explain {topic} to my friends",
    "That feeling when {topic} finally clicks",
    "Nobody warned me {topic} would be this hard",
    "Raise your hand if {topic} keeps you up at night",
    "The {topic} struggle is real and I'm tired of pretending it's not",
    "Can we talk about how exhausting {topic} actually is?",
    "I'm not crying over {topic}... okay maybe a little",
    "Day 1 of {topic} vs Day 100 — the glow-up is real",
    "Tell me you're obsessed with {topic} without telling me",
    "When someone says {topic} is easy... sure, Jan",
    "The {topic} reality nobody posts about",
  ],
  controversial: [
    "Unpopular opinion: {topic} is overrated",
    "Hot take: You don't need {topic} to succeed",
    "I'll probably get hate for this, but {topic} needs to change",
    "Everyone is wrong about {topic}. Fight me.",
    "The {topic} industry doesn't want you to know this",
    "Stop glorifying {topic}. Here's what actually matters.",
    "Your favorite {topic} guru is lying to you",
    "{topic} gatekeepers are going to hate this post",
    "I said what I said: {topic} is broken",
    "The uncomfortable truth about {topic} that nobody admits",
    "Why I stopped following {topic} trends — and you should too",
    "Controversial {topic} opinion: the old way was better",
  ],
  educational: [
    "Here are 5 {topic} tips you'll wish you knew sooner",
    "{topic} 101: Everything beginners get wrong",
    "The complete guide to {topic} in under 60 seconds",
    "Save this {topic} breakdown — you'll need it later",
    "How to master {topic} in 3 simple steps",
    "The {topic} cheat sheet nobody shared with you",
    "{topic} mistakes to avoid in 2026",
    "What 10 years of {topic} taught me in one post",
    "Bookmark this {topic} guide before it disappears",
    "The exact {topic} framework I use every single day",
    "If you're new to {topic}, start here",
    "Free {topic} knowledge that courses charge $997 for",
  ],
  storytelling: [
    "3 years ago, I knew nothing about {topic}. Here's my journey.",
    "The moment I almost gave up on {topic} changed everything",
    "I started {topic} with zero followers. Here's what happened.",
    "My biggest {topic} failure taught me the most valuable lesson",
    "From zero to mastering {topic} — the story nobody expected",
    "The day I discovered {topic} was the day everything shifted",
    "Here's the {topic} story I've been afraid to share",
    "They told me {topic} was impossible. 365 days later...",
    "I went all-in on {topic}. This is what I'd do differently.",
    "The {topic} decision that changed the trajectory of my life",
    "Behind the scenes of my {topic} journey — the real version",
    "From failing at {topic} to teaching it. Let me explain.",
  ],
}

// --- Content type modifiers ---

const contentTypeModifiers: Record<string, string[]> = {
  reel: [
    "Watch till the end for the {topic} reveal",
    "Wait for it... the {topic} plot twist is coming",
  ],
  carousel: [
    "Swipe to see the full {topic} breakdown",
    "Slide 5 has the {topic} secret that changes everything",
  ],
  "single-image": [
    "Read this caption if you care about {topic}",
    "One image. One {topic} truth. Screenshot this.",
  ],
  story: [
    "Quick {topic} tip before this disappears",
    "Tap through for the {topic} hack you need today",
  ],
}

// --- Generation logic ---

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const randomBytes = new Uint32Array(1)
    crypto.getRandomValues(randomBytes)
    const j = randomBytes[0] % (i + 1)
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

function generateHooks(topic: string, contentType: string, hookStyle: string): string[] {
  const patterns = hookPatterns[hookStyle] || hookPatterns.shocking
  const modifiers = contentTypeModifiers[contentType] || []

  // Combine style patterns with content-type modifiers
  const allPatterns = [...patterns, ...modifiers]
  const shuffled = shuffleArray(allPatterns)

  // Pick 10 unique hooks
  return shuffled.slice(0, 10).map((pattern) => {
    // Capitalize first letter of topic for beginning-of-sentence usage
    const capitalizedTopic = topic.charAt(0).toUpperCase() + topic.slice(1)
    return pattern
      .replace(/^\{topic\}/, capitalizedTopic)
      .replace(/\{topic\}/g, topic)
  })
}

// --- Page component ---

function HookGeneratorPage() {
  const [topic, setTopic] = useState("")
  const [contentType, setContentType] = useState("reel")
  const [hookStyle, setHookStyle] = useState("shocking")
  const [hooks, setHooks] = useState<string[] | null>(null)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)

  const isValid = topic.trim().length > 0

  const handleGenerate = () => {
    if (!isValid) return
    setHooks(generateHooks(topic.trim(), contentType, hookStyle))
    setCopiedIndex(null)
  }

  const handleGenerateMore = () => {
    if (!isValid) return
    setHooks(generateHooks(topic.trim(), contentType, hookStyle))
    setCopiedIndex(null)
  }

  const handleReset = () => {
    setTopic("")
    setContentType("reel")
    setHookStyle("shocking")
    setHooks(null)
    setCopiedIndex(null)
  }

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const textarea = document.createElement("textarea")
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  const copyAllHooks = async () => {
    if (!hooks) return
    const allText = hooks.map((h, i) => `${i + 1}. ${h}`).join("\n")
    try {
      await navigator.clipboard.writeText(allText)
    } catch {
      const textarea = document.createElement("textarea")
      textarea.value = allText
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }
    setCopiedIndex(-1)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>The Psychology Behind Instagram Hooks</SectionHeading>
            <Paragraph>
              The human brain decides whether to keep watching or keep scrolling in under 3 seconds. Neuroscientists call this the "thumb-stopping moment" — the instant where your subconscious evaluates a piece of content and either flags it as worth your attention or dismisses it entirely. This decision happens faster than conscious thought, which means your hook needs to bypass rational analysis and trigger an instinctive emotional response. Understanding the psychological patterns that make hooks work is the difference between content that reaches 100 people and content that reaches 100,000.
            </Paragraph>
            <Paragraph>
              <strong className="text-neutral-900 dark:text-white">Curiosity gaps</strong> are one of the most powerful hook mechanisms. When you present partial information — "The one mistake that kills 90% of Instagram accounts" — the brain experiences an uncomfortable tension between what it knows and what it wants to know. This tension can only be resolved by continuing to watch or read, which is exactly what drives engagement. Curiosity gaps work because the human mind is hardwired to seek closure and complete incomplete patterns.
            </Paragraph>
            <Paragraph>
              <strong className="text-neutral-900 dark:text-white">Pattern interrupts</strong> exploit the brain's tendency to filter out predictable content. When every post in someone's feed follows the same format, anything that breaks the pattern — a bold claim, an unusual visual, a counterintuitive statement — triggers a novelty response that demands attention. This is why hooks like "Everything you know about hashtags is wrong" outperform safe, predictable openings. The brain literally cannot ignore something that contradicts its existing mental models.
            </Paragraph>
            <Paragraph>
              <strong className="text-neutral-900 dark:text-white">Emotional triggers</strong> work because emotion processes faster than logic. Hooks that evoke surprise, fear, excitement, or empathy activate the amygdala before the prefrontal cortex has time to rationalize scrolling past. <strong className="text-neutral-900 dark:text-white">Social proof</strong> — referencing what "top creators" or "successful brands" do — leverages our tribal instinct to follow what others validate. And <strong className="text-neutral-900 dark:text-white">fear of missing out (FOMO)</strong> creates urgency by implying that not engaging means being left behind. Each of these psychological levers, when used ethically and authentically, dramatically increases the stopping power of your opening line.
            </Paragraph>
            <Paragraph>
              The best hooks combine two or more of these mechanisms simultaneously. A hook like "I was today years old when I learned that 80% of creators are doing hashtags wrong" layers a curiosity gap (what are they doing wrong?), social proof (80% implies authoritative data), and a pattern interrupt (challenges a common belief) into a single sentence. Understanding these psychological foundations does not make your content manipulative — it makes it effective at earning the attention your message deserves.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>The 3-Second Rule</SectionHeading>
            <Paragraph>
              Instagram's algorithm is obsessed with early engagement signals. Within the first 3 seconds of someone encountering your content, the platform is already collecting data: Did they pause scrolling? Did they tap to expand the caption? Did they start watching your Reel or swipe to slide two of your carousel? If the answer is yes, Instagram interprets these micro-interactions as strong quality signals and begins pushing your content to wider audiences — first to more of your followers, then to Explore, then to suggested content feeds.
            </Paragraph>
            <StatGrid stats={[
              { value: "3 sec", label: "Attention Window", sublabel: "Time to hook or lose a viewer" },
              { value: "100x", label: "Reach Multiplier", sublabel: "Strong hook vs. weak hook" },
              { value: "#1", label: "Highest-Leverage Element", sublabel: "In your entire content workflow" },
            ]} />
            <Paragraph className="mt-4">
              If viewers swipe past in under 3 seconds, the opposite happens. The algorithm reads that rapid dismissal as a negative signal and suppresses distribution. Your content gets shown to fewer people, engagement drops, and the post effectively dies. This means your hook is not just a creative choice — it is the single variable that determines whether 100 people or 100,000 people see your content. The rest of your post can be brilliant, but if the hook fails, nobody sticks around long enough to find out.
            </Paragraph>
            <Paragraph>
              This is why elite creators treat hooks as the highest-leverage element of their entire content workflow. Many top-performing accounts spend more time writing and testing their opening line than they spend on the entire rest of the caption or script. The 3-second rule applies equally to Reels (the first frame and first spoken words), carousels (the cover slide headline), and feed posts (the first line of the caption before the "more" truncation). Every format has its own version of the 3-second test, and mastering it is non-negotiable for growth in 2026.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>6 Proven Hook Styles (and When to Use Each)</SectionHeading>
            <FeatureGrid columns={3} items={[
              { icon: "\uD83D\uDCA5", title: "Shocking / Bold", description: "Bold hooks make a statement so surprising or provocative that the viewer has no choice but to stop and engage. They work best for Reels and posts tackling controversial topics or challenging widely-held beliefs. The key is backing up the bold claim with substance in the body of your content — otherwise you lose trust." },
              { icon: "\uD83E\uDD14", title: "Curiosity-Driven", description: "Curiosity hooks tease a payoff without revealing it, creating an information gap the viewer needs to close. They are perfect for carousels and tutorial Reels where you want maximum watch time. Phrases like \"The one thing nobody tells you about...\" leave the viewer with an unanswered question." },
              { icon: "\uD83D\uDE0A", title: "Relatable / Personal", description: "Relatability hooks tap into shared experiences that make the viewer think \"that is literally me.\" They build connection and community, driving saves and shares because people want to send them to friends who feel the same way. Especially powerful for community building." },
              { icon: "\uD83D\uDD25", title: "Controversial / Hot Take", description: "Controversial hooks are engineered to spark debate, which makes them comment magnets. When someone disagrees with your opening line, they feel compelled to tell you — and that comment drives engagement that boosts your reach. Use strategically for topics where you have a genuine, defensible opinion." },
              { icon: "\uD83D\uDCDA", title: "Educational / Value", description: "Educational hooks promise actionable knowledge and position you as an authority in your niche. Ideal for how-to content, tip threads, and in-depth breakdowns. \"5 mistakes killing your engagement in 2026\" signals immediate, practical value and generates the highest save rates." },
              { icon: "\uD83D\uDCD6", title: "Storytelling / Narrative", description: "Storytelling hooks open a narrative arc that the viewer wants to see through to the end. \"3 years ago I had zero followers and no idea what I was doing\" draws the viewer into a journey with an implied transformation. The human brain is wired for narrative, making these uniquely powerful." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Hook Patterns That Go Viral</SectionHeading>
            <Paragraph>
              Certain hook patterns appear again and again in viral content because they reliably trigger the psychological responses that drive engagement. While you should always adapt these patterns to your unique voice and niche, understanding the proven frameworks gives you a massive head start. Here are the patterns that consistently produce high-performing content across every niche on Instagram:
            </Paragraph>
            <TipList tips={[
              { title: "\"Stop doing X if you want Y\"", description: "Combines a pattern interrupt with implied insider knowledge. The viewer assumes they might be making the mistake and needs to find out." },
              { title: "\"The truth about X nobody talks about\"", description: "Creates a curiosity gap by implying hidden knowledge. Works exceptionally well for industry-specific content where viewers feel they are getting exclusive insight." },
              { title: "\"I was today years old when...\"", description: "Leverages the relatable shock of learning something late. This pattern signals that the information is both surprising and universally useful." },
              { title: "\"POV: [scenario]\"", description: "Invites the viewer into a shared experience, making the content feel personal and immediately relatable. Extremely effective for Reels and story content." },
              { title: "\"This changed everything about my X\"", description: "Implies a transformation story with a specific turning point, triggering curiosity about what that turning point was." },
              { title: "\"X mistakes you're making with Y\"", description: "Numbered lists promise scannable, actionable value. The word \"mistakes\" adds urgency because nobody wants to be doing something wrong." },
              { title: "\"Here's why your X isn't working\"", description: "Directly addresses a pain point the viewer is likely experiencing, making it almost impossible to scroll past without finding out the reason." },
              { title: "\"I tested X so you don't have to\"", description: "Positions you as someone who has done the hard work, offering the viewer a shortcut to knowledge. This builds trust and authority simultaneously." },
              { title: "\"The X hack that [impressive result]\"", description: "Combines the promise of a shortcut with a specific, desirable outcome. The more specific and measurable the result, the more compelling the hook." },
              { title: "\"They told me X was impossible. Here's what happened.\"", description: "Opens a narrative arc with built-in conflict and an implied victory. Viewers want to see the underdog win." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>How to Test and Iterate on Hooks</SectionHeading>
            <Paragraph>
              Writing great hooks is not a talent you are born with — it is a skill you develop through systematic testing and iteration. The most successful creators do not rely on a single flash of inspiration. Instead, they write 5 to 10 different hooks for every piece of content and then select the strongest one. This process forces you to explore different angles, psychological triggers, and framings for the same topic, and over time it trains your instinct for what works.
            </Paragraph>
            <Paragraph>
              A/B testing is the foundation of hook improvement. Post similar content with different hooks on different days or across different formats (a Reel hook versus a carousel cover headline) and compare performance. Pay close attention to Reels retention graphs in Instagram Insights — the shape of the curve in the first 3 seconds tells you exactly whether your hook is working. A steep drop-off means the hook failed to hold attention. A flat or rising line means viewers are sticking around, which is the clearest possible signal that your opening resonated.
            </Paragraph>
            <Paragraph>
              Use the hook-body-payoff framework to evaluate your content structure. The hook grabs attention, the body delivers value, and the payoff rewards the viewer for staying until the end. If your hook promises something specific — "3 mistakes killing your reach" — the body must deliver those 3 mistakes, and the payoff should include an actionable fix or surprising insight. Broken promises between hook and content destroy viewer trust and tank your retention metrics over time. Consistency between what you promise and what you deliver is what builds a loyal, engaged audience that the algorithm rewards.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>Turning Hook Engagement into Revenue</SectionHeading>
            <Paragraph>
              Scroll-stopping hooks do more than inflate your view count — they are the top of a conversion funnel that, when built correctly, drives real revenue. When your hook is strong enough to stop the scroll and your content delivers genuine value, viewers take action. They comment "HOW?" on your Reel. They type "LINK?" under your carousel. They reply to your Story with "I need this." Each of those comments and replies represents a person in an active buying mindset — someone who has self-identified as interested in what you are offering.
            </Paragraph>
            <Paragraph>
              This is where DM automation transforms engagement into income. Tools like Zapify allow you to set up keyword-triggered automations that instantly send a personalized direct message to anyone who comments a specific word on your post. When someone comments "GUIDE" on your hook-driven Reel, they receive your lead magnet, product link, or free resource in their DMs within seconds — no manual effort required. The speed of this response is critical: the window between "I'm interested" and "I've moved on" is measured in minutes, not hours.
            </Paragraph>
            <Paragraph>
              The flywheel effect is powerful. Strong hooks drive views. Views drive comments. Comments trigger automated DMs. DMs deliver value and capture leads. And every comment also signals to the algorithm that your content is engaging, which pushes it to even more people — generating more comments, more DMs, and more conversions. By combining exceptional hook writing with smart automation, you build a system where every piece of content you publish actively works to grow your audience and your revenue simultaneously. The hook is not just creative expression — it is the entry point to your entire business engine.
            </Paragraph>
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        {/* Form Card */}
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!hooks ? (
            <div className="space-y-4">
              <TextField
                label="Topic"
                value={topic}
                onChange={setTopic}
                placeholder="Enter the main topic of your content"
                required
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SelectField
                  label="Content Type"
                  value={contentType}
                  onChange={setContentType}
                  options={contentTypeOptions}
                  required
                  placeholder="Select content type"
                />

                <SelectField
                  label="Hook Style"
                  value={hookStyle}
                  onChange={setHookStyle}
                  options={hookStyleOptions}
                  required
                  placeholder="Select hook style"
                />
              </div>

              <SubmitButton onClick={handleGenerate} disabled={!isValid}>
                Generate Hooks
              </SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Topic & style summary */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1 text-xs font-semibold text-neutral-600 dark:text-neutral-400">
                  {topic}
                </span>
                <span className="inline-flex items-center rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1 text-xs font-semibold text-neutral-600 dark:text-neutral-400">
                  {contentTypeOptions.find((o) => o.value === contentType)?.label}
                </span>
                <span className="inline-flex items-center rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1 text-xs font-semibold text-neutral-600 dark:text-neutral-400">
                  {hookStyleOptions.find((o) => o.value === hookStyle)?.label}
                </span>
              </div>

              {/* Hook cards */}
              <div className="space-y-3">
                {hooks.map((hook, index) => (
                  <div
                    key={index}
                    className="group flex items-start gap-3 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 px-4 py-3 shadow-sm hover:border-neutral-300 dark:hover:border-neutral-700 transition-colors"
                  >
                    <span className="shrink-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-brand-accent/10 via-brand-accent/10 to-brand-accent/10 text-xs font-bold text-neutral-500 dark:text-neutral-400">
                      {index + 1}
                    </span>
                    <p className="flex-1 min-w-0 text-sm sm:text-base text-neutral-900 dark:text-white leading-relaxed pt-0.5">
                      {hook}
                    </p>
                    <button
                      onClick={() => copyToClipboard(hook, index)}
                      className={`shrink-0 h-8 px-3 rounded-lg text-xs font-semibold transition-all duration-300 ${
                        copiedIndex === index
                          ? "bg-green-500 text-white"
                          : "bg-brand-accent text-white shadow-[0_2px_8px_rgba(244,63,94,0.15)] hover:shadow-[0_4px_14px_rgba(244,63,94,0.15)] hover:-translate-y-0.5"
                      }`}
                    >
                      {copiedIndex === index ? "Copied!" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>

              {/* Copy All button */}
              <button
                onClick={copyAllHooks}
                className={`w-full h-11 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  copiedIndex === -1
                    ? "bg-green-500 text-white"
                    : "border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
                }`}
              >
                {copiedIndex === -1 ? "All Hooks Copied!" : "Copy All Hooks"}
              </button>

              {/* Action buttons */}
              <div className="grid grid-cols-2 gap-3">
                <SubmitButton onClick={handleGenerateMore} disabled={false}>
                  Generate More
                </SubmitButton>
                <ResetButton onClick={handleReset}>Change Topic</ResetButton>
              </div>
            </div>
          )}
        </div>

        {/* Empty state hint */}
        {!hooks && (
          <div className="mt-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-700 p-8 text-center">
            <p className="text-sm text-neutral-400 dark:text-neutral-500">
              Enter a topic and select your content type and hook style to generate 10 scroll-stopping hooks
            </p>
          </div>
        )}
      </div>
    </ToolPageLayout>
  )
}
